import { Tooltip } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { jwtSelector } from "src/common/state/selectors/authSelector";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { ButtonIcon } from "src/common/components/Button";
import PlaceholderWithIcon from "src/common/components/PlaceholderWithIcon/PlaceholderWithIcon";
import { Checkbox } from "src/common/components/Input";
import CheckAll from "src/common/components/UsersTable/CheckAll";
import type { IAllowedItem } from "src/common/components/UsersTable/DisplayAllowedItems";
import { DisplayAllowedItems } from "src/common/components/UsersTable/DisplayAllowedItems";
import { UserCrudButtons } from "src/common/components/UsersTable/UserCrudButtons";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import {
  getSearchIndex,
  fetchSearchIndex,
  getSelectedProjectGroup,
} from "src/features/projectGroups/state/projectGroupsSlice";
import {
  fetchUserDetails,
  getEndusers,
  getTeamAsAdmin,
  setDeleteUsersDraft,
} from "src/features/team/state/teamSlice";
import { SearchAutocomplete } from "src/common/components/SearchAutocomplete/SearchAutocomplete";
import { DeleteElementsSelectedFromList } from "src/common/components/DeleteElementsSelectedFromList/DeleteElementsSelectedFromList";
import {
  getConfirmationModalStateShow,
  openConfirmationModal,
} from "src/common/state/slice/modal/modalSlice";
import { CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdminPermissionsList";
import SimplifiedUserIcon from "../../assets/simplified-user-round-marker.png";
import RoundImagePreview from "src/features/projects/components/RoundImagePreview";
import TableComp from "src/common/components/Table/Table";

// Component IDs for loading states
export const PROJECT_GROUP_LEARNERS_LIST_COMPONENT = "projectGroupLearnersListComponent";
export const PROJECT_GROUP_LEARNERS_TABLE_COMPONENT = "projectGroupLearnersTableComponent";

// Simplified user email pattern constant
const SIMPLIFIED_USER_EMAIL_PATTERN = "@sneakysneaky.ua";

// Table height calculation constant (golden ratio approximation)
const TABLE_HEIGHT_RATIO = 2 / 3;

/**
 * Story 19.2: Project-Group Learners List
 * Displays learners (endusers) who have access to modules within the current project-group
 */
const ProjectGroupLearnersList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: projectGroupId } = useParams();

  const searchIndex = useAppSelector(getSearchIndex);
  const endusers = useAppSelector(getEndusers);
  const confirmationModalStateShow = useAppSelector(getConfirmationModalStateShow);
  const jwt = useAppSelector(jwtSelector);
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);

  // Renamed from 'filteredEndusers' to 'displayedEndusers' for clarity (represents final displayed list)
  const [displayedEndusers, setDisplayedEndusers] = useState(endusers);
  const [isCheck, setIsCheck] = useState<Array<any>>([]);
  const [filterByText, setFilterByText] = useState("");
  const [tableHeight, setTableHeight] = useState(0);

  // Useful to determine if any filter has been applied
  const maxRowsLengthRef = useRef(0);

  // Filter projects to only those in the current project-group
  // Memoize projectIds Set to avoid recalculation in row rendering
  const projectsInThisProjectGroup = useMemo(() => {
    return searchIndex.filter(
      (item) => item.type === "project" && item.projectGroupId === Number(projectGroupId),
    );
  }, [searchIndex, projectGroupId]);

  const projectIds = useMemo(() => {
    return new Set(projectsInThisProjectGroup.map((p) => String(p.id)));
  }, [projectsInThisProjectGroup]);

  // Filter endusers to only those belonging to the current project-group
  // Renamed from 'endusersInProjectGroup' to 'endusersByProjectGroup' for clarity
  const endusersByProjectGroup = useMemo(() => {
    // Prevent rendering with partial data during initial load
    if (!searchIndex.length || !endusers.length) return [];

    return endusers.filter((user) => {
      // Check if user has any permission to projects in this project-group
      // Use defensive pattern: String(p?.project ? p.project.id : "")
      return user.permissions?.some((perm) => {
        const projectId = String(perm?.project ? perm.project.id : "");
        return projectId && projectIds.has(projectId);
      });
    });
  }, [endusers, projectIds, searchIndex.length]);

  // Update the state when the store was updated (typically, by an API call)
  useEffect(() => {
    setDisplayedEndusers(endusersByProjectGroup);
    setIsCheck([]);
    // Track max rows to distinguish between "no results" and "filtered to zero"
    maxRowsLengthRef.current = Math.max(maxRowsLengthRef.current, endusersByProjectGroup.length);
  }, [endusersByProjectGroup]);

  // FILTER logic - search by text
  const handleOnSearch = (string: string) => {
    // Reset selection on search
    setIsCheck([]);
    if (string === "") {
      setFilterByText("");
    }
  };

  const handleOnSelect = (item: { name: string }) => {
    setFilterByText(item.name);
  };

  useEffect(() => {
    let filteredUsers = endusersByProjectGroup;

    if (filterByText) {
      filteredUsers = filteredUsers.filter((user) =>
        user.email.toLowerCase().includes(filterByText.toLowerCase()),
      );
    }

    setDisplayedEndusers(filteredUsers);
    // Don't update maxRowsLengthRef here - filtering shouldn't affect "ever had data" tracking
  }, [filterByText, endusersByProjectGroup]);

  // Calculate table height
  useEffect(() => {
    const calculateHeight = () => {
      const divinaProportione = Math.round(window.innerHeight * TABLE_HEIGHT_RATIO);
      setTableHeight(divinaProportione);
    };

    calculateHeight();

    window.addEventListener("resize", calculateHeight);
    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  }, []);

  // Reset selection when modal closes
  useEffect(() => {
    if (!confirmationModalStateShow) {
      setIsCheck([]);
    }
  }, [confirmationModalStateShow]);

  const handleSelectAll = () => {
    if (isCheck.length > 0) {
      setIsCheck([]);
    } else {
      setIsCheck(displayedEndusers.map((li) => String(li.id)));
    }
  };

  const handleClick = (e: any) => {
    const { id, checked } = e.target;

    // State update logic
    if (checked) {
      const nextValues = [...isCheck, id];
      setIsCheck(nextValues);
    } else {
      setIsCheck(isCheck.filter((item) => item !== id));
    }
  };

  // Fetch data on mount and when navigating back to this page
  useEffect(() => {
    // Don't make API calls until we have a JWT
    if (!jwt) {
      return;
    }

    void dispatch(fetchSearchIndex({}));
    void dispatch(getTeamAsAdmin({ componentId: PROJECT_GROUP_LEARNERS_LIST_COMPONENT }));
  }, [dispatch, jwt, location.key]);

  // If auth_type is "none", learners management is not applicable.
  // This guard must stay below every hook so hook order is identical on every
  // render (Rules of Hooks) — returning before the hooks above crashes React
  // with "rendered fewer hooks than expected" when auth_type changes.
  if (selectedProjectGroup?.auth_type === "none") {
    return (
      <div className="w-full h-1/2 flex justify-center mt-16">
        <PlaceholderWithIcon
          title={t("pages.projectGroup.learners.anonymousDisclaimerTitle")}
          text={t("pages.projectGroup.learners.anonymousDisclaimerText")}
          heroIcon="InformationCircleIcon"
          className="flex flex-col items-center h-[6pc]"
        />
      </div>
    );
  }

  const colHeaders = [
    "",
    t("general.firstname"),
    t("general.lastname"),
    t("general.identifier"),
    t("pages.projectGroup.learners.modules"),
    t("general.actions"),
  ];

  const rows = displayedEndusers.map((enduser, i: number) => {
    // Count modules accessible in this project-group
    // Use defensive pattern and pre-calculated projectIds Set
    const moduleCount =
      enduser.permissions?.filter((perm) => {
        const projectId = String(perm?.project ? perm.project.id : "");
        return projectId && projectIds.has(projectId);
      }).length || 0;

    // Calculate total projects in this project-group
    const totalProjectsInGroup = projectsInThisProjectGroup.length;

    // User has access to everything if they have all projects in this group
    const hasAccessToEverything = moduleCount === totalProjectsInGroup;

    const enduserIsSimplified = enduser.email.includes(SIMPLIFIED_USER_EMAIL_PATTERN);

    // Build list of allowed items (only projects in this project-group)
    const allowedItems: IAllowedItem[] = [];
    enduser.permissions?.forEach((p) => {
      // Use defensive pattern: String(p?.project ? p.project.id : "")
      const projectId = String(p?.project ? p.project.id : "");
      if (projectId && projectIds.has(projectId) && p.project?.name) {
        allowedItems.push({
          name: p.project.name,
          published: p.project.status === "Published",
        });
      }
    });

    return {
      checkbox: (
        <Checkbox
          checked={isCheck.includes(String(enduser.id))}
          key={`${i}-${enduser.id}`}
          id={`${enduser.id}`}
          value={Number(enduser.id)}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e);
          }}
        />
      ),
      avatar: <RoundImagePreview mediaId_OrIsItAnObjectLol={enduser.source_avatar} />,
      firstName: !enduserIsSimplified ? (
        enduser.firstname
      ) : (
        <div className="flex relative">
          <Tooltip title={t("general.userWithSimplifiedLogin")}>
            <div className="absolute -left-8 top-[2px] flex items-center justify-center rounded-full bg-blue-500 text-white">
              <img src={SimplifiedUserIcon} className="w-4 h-4" />
            </div>
          </Tooltip>
          <span> {enduser.firstname}</span>
        </div>
      ),
      lastName: enduser.lastname,
      email: enduser.email,
      projectGroups: (
        <DisplayAllowedItems
          itemsAreProjectGroups={false}
          hasAccessToEverything={hasAccessToEverything}
          allowedItems={allowedItems}
        />
      ),
      crudButtons: (
        <UserCrudButtons
          editDisabled={enduserIsSimplified}
          editDisabledTooltip={t("pages.participants.simplifiedUsersCannotBeEdited")}
          onClickEdit={() => {
            void dispatch(
              fetchUserDetails({
                ...enduser,
                componentId: CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT,
              }),
            );
            navigate(`/courses/${projectGroupId}/learners/${enduser.id}`);
          }}
          onClickDelete={() => {
            dispatch(
              setDeleteUsersDraft({
                type: "delete_enduser",
                usersToBeDeleted: [{ id: enduser.id, email: enduser.email }],
              }),
            );
            dispatch(openConfirmationModal("confirm-delete-users"));
          }}
        />
      ),
    };
  });

  return (
    <>
      <div className="flex flex-col items-center w-full h-full">
        <div className="flex mt-4 w-[96%] justify-between items-end">
          <SearchAutocomplete
            items={displayedEndusers.map((user) => {
              return { name: user.email };
            })}
            handleOnSearch={handleOnSearch}
            handleOnSelect={handleOnSelect}
          />
          <ButtonIcon
            icon="PlusIcon"
            styled="btn-primary-fill h-12"
            text={t("pages.projectGroup.learners.addNewLearners")}
            onClick={() => navigate(`/courses/${projectGroupId}/learners/new`)}
          />
        </div>
        <DeleteElementsSelectedFromList
          className="self-start ml-12 mt-2"
          isCheck={isCheck}
          selectedTextSingular={t("pages.participants.selectedUserSingular")}
          selectedTextPlurial={t("pages.participants.selectedUserPlurial")}
          deleteButtonTextSingular={t("pages.participants.deleteSelectedUserSingular")}
          deleteButtonTextPlurial={t("pages.participants.deleteSelectedUserPlural")}
          deleteCallback={() => {
            dispatch(
              setDeleteUsersDraft({
                type: isCheck.length > 1 ? "delete_endusers" : "delete_enduser",
                usersToBeDeleted: endusers.filter((c) => isCheck.includes(String(c.id))),
              }),
            );
            dispatch(openConfirmationModal("confirm-delete-users"));
          }}
        />
        <IsLoading
          componentId={PROJECT_GROUP_LEARNERS_LIST_COMPONENT}
          showSuccess={false}
          spinnerPlaceholder
        >
          <IsLoading
            componentId={PROJECT_GROUP_LEARNERS_TABLE_COMPONENT}
            showSuccess={false}
            spinnerPlaceholder
          >
            <TableComp
              key={tableHeight}
              style={{ maxHeight: `${tableHeight}px` }}
              className="w-[96%] mt-2 mb-2 personalize-scroll-visible"
              colHeaders={colHeaders}
              nonSortableColumns={[t("general.actions")]}
              rows={rows}
              disableCriteria={() => false}
              checkAll={
                <CheckAll
                  className={displayedEndusers.length === 0 ? "hidden" : ""}
                  checked={isCheck.length > 0}
                  onChange={handleSelectAll}
                />
              }
              listTofilter={displayedEndusers}
              selectFromListInTable
              setListOfItemsFilter={setDisplayedEndusers}
              isUserList={true}
              emptyPlaceholder={
                <div className="flex m-8 items-center justify-center">
                  {maxRowsLengthRef.current
                    ? t("pages.projectGroup.learners.noLearnersMatchFilters")
                    : t("pages.projectGroup.learners.noLearnersCTA")}
                </div>
              }
            />
          </IsLoading>
        </IsLoading>
      </div>
    </>
  );
};

export default ProjectGroupLearnersList;
